/**
 * 沙盒预览页（C-4.5）
 *
 * 路由：/admin/data-import/sandbox/:id · /team/data-import/sandbox/:id · /import/sandbox/:id
 *
 * 用户从 Step 7 完成后的"进入沙盒预览页"链接跳过来。
 * 展示这次沙盒导入写到 sandbox_job_id = id 的所有行（分页 + 表格）。
 *
 * 三个出口：
 *   - 正式导入：UPDATE sandbox_job_id = NULL（不可逆，但导入后还能走 C-5 撤销）
 *   - 全部撤销：DELETE WHERE sandbox_job_id = id（不可逆 — 沙盒数据消失）
 *   - 导出 Excel：sheet_to_csv → blob 下载
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Empty, message } from 'antd';
import { $ } from '../../lib/api';
import { PageHeader, Pill, Btn, StatsRow, StatCard } from '../../components/ui';
import { toast } from '../../lib/toast';

const TYPE_LABEL: Record<string, string> = {
  customers: '客户主表', follow_ups: '跟进记录', contracts: '合同',
  invoices: '发票', payments: '收款', shipments: '邮寄',
  materials: '学员资料', orders: '订单',
};

interface JobMeta {
  id: number;
  status: string;
  importType: string;
  fileName?: string | null;
  totalRows: number;
  successCount: number;
  failCount: number;
  skipCount: number;
  isSandbox: number | boolean;
  createdAt: string;
}

interface PreviewResp {
  items: Record<string, any>[];
  total: number;
  page: number;
  pageSize: number;
  type: string;
}

export function SandboxPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const jobId = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data: job } = useQuery({
    queryKey: ['imports-detail', jobId],
    queryFn: () => $.get<JobMeta>(`/imports/${jobId}`),
  });

  const { data: preview, isLoading } = useQuery({
    queryKey: ['imports-sandbox-preview', jobId, page],
    queryFn: () => $.get<PreviewResp>(`/imports/${jobId}/sandbox/preview`, { page, pageSize }),
    enabled: !!jobId && !!job?.isSandbox,
  });

  const commit = useMutation({
    mutationFn: () => $.post(`/imports/${jobId}/sandbox/commit`),
    onSuccess: (r: any) => {
      const total = Object.values(r?.committed ?? {}).reduce((a: number, n: any) => a + Number(n ?? 0), 0);
      // 关键修复：commit 后立即移除本页 cache，组件下一帧 unmount 前不会再 refetch /sandbox/preview
      // （否则后端会因为 job.isSandbox=0 返回 400 "该任务不是沙盒模式"，触发红色错误 toast）
      qc.removeQueries({ queryKey: ['imports-sandbox-preview', jobId] });
      qc.removeQueries({ queryKey: ['imports-detail', jobId] });
      // 跳目标页 — 客户类型跳客户列表，其他业务表数据跳导入中心（带 banner 参数）
      const targetType = preview?.type ?? job?.importType ?? '';
      const target = targetType === 'customers'
        ? `/customers?fromSandbox=${jobId}&imported=${total}`
        : `/admin/data-import?fromSandbox=${jobId}&imported=${total}&type=${targetType}`;
      nav(target, { replace: true });
    },
  });

  const discard = useMutation({
    mutationFn: () => $.post(`/imports/${jobId}/sandbox/discard`),
    onSuccess: (r: any) => {
      const total = Object.values(r?.discarded ?? {}).reduce((a: number, n: any) => a + Number(n ?? 0), 0);
      toast.success(`沙盒已撤销 · 删除 ${total} 行`);
      qc.invalidateQueries({ queryKey: ['imports-detail', jobId] });
      nav('/admin/data-import');
    },
  });

  const handleCommit = () => {
    Modal.confirm({
      title: '正式提交沙盒数据？',
      content: `这次沙盒共 ${preview?.total ?? '?'} 行 ${TYPE_LABEL[preview?.type ?? ''] ?? preview?.type}。提交后这些行会进入正式客户列表，所有销售员都能看到。`,
      okText: '确认正式提交',
      okType: 'primary',
      cancelText: '取消',
      onOk: () => commit.mutateAsync(),
    });
  };
  const handleDiscard = () => {
    Modal.confirm({
      title: '全部撤销沙盒数据？',
      content: `所有 ${preview?.total ?? '?'} 行将被永久删除，无法恢复。`,
      okText: '确认全部撤销',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => discard.mutateAsync(),
    });
  };
  const handleExport = () => {
    if (!preview?.items?.length) {
      message.info('沙盒为空，无需导出');
      return;
    }
    const headers = Object.keys(preview.items[0]);
    const csv = [
      headers.join(','),
      ...preview.items.map((row) =>
        headers.map((h) => {
          const v = row[h];
          if (v === null || v === undefined) return '';
          const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
          return `"${s.replace(/"/g, '""')}"`;
        }).join(','),
      ),
    ].join('\n');
    // UTF-8 BOM 让 Excel 打开不乱码
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `sandbox-${jobId}-${preview.type}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`已导出 ${preview.items.length} 行（当前页）`);
  };

  const items = preview?.items ?? [];
  const headers = useMemo(() => {
    if (items.length === 0) return [];
    // 隐藏内部字段（id 留着方便定位）
    return Object.keys(items[0]).filter((k) =>
      !['deleted_at', 'sandbox_job_id', 'ai_recognized_text', 'risk_flags', 'materials_sent', 'ai_profile_json'].includes(k),
    );
  }, [items]);

  if (!job) return <div className="work-content">加载中…</div>;

  if (!job.isSandbox) {
    return (
      <>
        <PageHeader title="沙盒预览" breadcrumb={['数据导入', '沙盒预览']} />
        <div className="work-content">
          <div className="import-card">
            <Empty description={`任务 #${jobId} 不是沙盒模式`} />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Link to="/admin/data-import"><Btn kind="primary">返回导入中心</Btn></Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const isCancelled = job.status === 'cancelled' || job.status === 'rolled_back';

  return (
    <>
      <PageHeader
        title={`沙盒预览 #${jobId}`}
        subtitle={`${TYPE_LABEL[job.importType] ?? job.importType} · ${job.fileName ?? ''}`}
        breadcrumb={['数据导入', '沙盒预览']}
        actions={
          !isCancelled && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn kind="default" onClick={handleExport}>导出 Excel</Btn>
              <Btn kind="default" onClick={handleDiscard} disabled={discard.isPending}>
                {discard.isPending ? '撤销中…' : '全部撤销'}
              </Btn>
              <Btn kind="primary" onClick={handleCommit} disabled={commit.isPending}>
                {commit.isPending ? '提交中…' : '正式导入'}
              </Btn>
            </div>
          )
        }
      />
      <div className="work-content">
        {/* 顶部 banner */}
        <div
          className="import-card"
          style={{ marginBottom: 12, background: 'var(--primary-bg, #e6f7ff)', border: '1px solid var(--primary)' }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ fontSize: 22 }}>🧪</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>
                这是沙盒预览数据。正式列表看不到这些行。
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                检查数据无误后点 <strong>正式导入</strong> → 数据进入生产库 ·
                有错点 <strong>全部撤销</strong> → 沙盒数据永久删除 ·
                想本地分析点 <strong>导出 Excel</strong>
              </div>
            </div>
            <Pill tone={isCancelled ? 'default' : 'warning'}>
              {isCancelled ? '已撤销' : '沙盒中'}
            </Pill>
          </div>
        </div>

        <StatsRow>
          <StatCard label="本沙盒行数" value={preview?.total ?? 0} unit="行" />
          <StatCard label="成功" value={job.successCount} unit="行" valueColor="success" />
          <StatCard label="跳过" value={job.skipCount} unit="行" />
          <StatCard label="失败" value={job.failCount} unit="行" valueColor="danger" />
        </StatsRow>

        <div className="import-card" style={{ marginTop: 12 }}>
          <div className="import-card-title">
            数据明细
            <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-3)', fontWeight: 400 }}>
              第 {page} 页 · 每页 {pageSize} · 共 {preview?.total ?? 0} 行
            </span>
          </div>
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</div>
          ) : items.length === 0 ? (
            <Empty description="沙盒为空 — 可能已被撤销或正式提交" />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="ck-table" style={{ width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    {headers.map((h) => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, i) => (
                    <tr key={row.id ?? i}>
                      {headers.map((h) => (
                        <td key={h} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formatCell(row[h])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* 分页 */}
          {preview && preview.total > pageSize && (
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 8 }}>
              <Btn kind="text" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                ← 上一页
              </Btn>
              <span style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text-3)' }}>
                {page} / {Math.ceil(preview.total / pageSize)}
              </span>
              <Btn kind="text"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(preview.total / pageSize)}>
                下一页 →
              </Btn>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function formatCell(v: any): string {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  // 日期截断 .000Z 等
  if (s.includes('T') && s.includes('Z')) {
    try {
      const d = new Date(s);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { /* fall through */ }
  }
  return s;
}
function pad(n: number) { return String(n).padStart(2, '0'); }
